<template lang="html">
  <div ref="jobList">
    <div class="fsd-list__item--skeleton" />
    <transition v-if="!resultsLoading && resultsPagination" name="fade">
      <div v-tracking-context="{ content_element: 'result_list' }">
        <ul>
          <li
            v-if="hasJobPinnedId"
            :key="jobPinnedId"
            v-tracking-context="{ event_element_pos: 1 }"
            class="fsd-list__item"
            :class="{
              'fsd-list__item--selected': jobPinnedId === resultsJobSelected,
              'card--expired': jobPinned.expired
            }"
          >
            <span
              class="fsd-list__item-anchor"
              :class="{ 'fsd-list__item-anchor--sticky': isSearchSticky }"
            />
            <job-teaser
              :job="jobPinned"
              :bookmarked="bookmarksMap[jobPinnedId]"
              :selected="jobPinnedId === resultsJobSelected"
              @select="onSelect(jobPinned)"
              @bookmark-toggle="bookmarksToggle(jobPinnedId)"
            />
          </li>
          <template v-for="(job, idx) in jobs">
            <li
              :key="job.id"
              v-tracking-context="{ event_element_pos: idx + idxOffset }"
              class="fsd-list__item"
              :class="{
                'fsd-list__item--selected': job.id === resultsJobSelected
              }"
            >
              <span
                class="fsd-list__item-anchor"
                :class="{ 'fsd-list__item-anchor--sticky': isSearchSticky }"
              />
              <job-teaser
                :job="job"
                :bookmarked="bookmarksMap[job.id]"
                :selected="job.id === resultsJobSelected"
                @select="onSelect(job)"
                @bookmark-toggle="bookmarksToggle(job.id)"
              />
            </li>
            <li
              v-if="
                resultsMatchingProfile &&
                (idx === 7 ||
                  (results.length < 8 && idx === results.length - 1))
              "
              :key="`profile-${job.id}`"
              class="fsd-list__item"
            >
              <!-- eslint-disable vue/no-v-html -->
              <div v-html="resultsMatchingProfile" />
            </li>
          </template>
        </ul>
        <div
          v-if="resultsPagination.current < resultsPagination.total"
          class="jobs__show-more flex-container align-center"
        >
          <a
            v-scrolls-into-view="{ handler: resultsPaginationNext }"
            :href="resultsPagination.nextUrl"
            class="button button--bordered"
            @click.prevent="resultsPaginationNext"
          >
            <span class="typo-12">
              {{ 'client.fused.jobs.show_more' | t }}
            </span>
          </a>
        </div>
        <div />
      </div>
    </transition>

    <transition
      v-if="resultsCount === 0 && !resultsLoading && !jobPinned"
      name="filter"
    >
      <div class="card card--no-results typo-sans">
        <div class="card__headline">
          {{ 'client.fused.jobs.no_results.headline' | t }}
        </div>

        <div
          v-if="termSuggestions.length > 0"
          class="row search-suggestions clearfix"
        >
          <div class="left search-suggestions__label">
            {{ 'client.fused.jobs.no_results.did_you_mean' | t }}
          </div>

          <div class="left">
            <ul v-for="suggestion in termSuggestions" :key="suggestion.name">
              <li>
                <a
                  :href="suggestion.url"
                  class="search-suggestions__link ac-link--primary"
                  >{{ suggestion.name }}</a
                >
              </li>
            </ul>
          </div>
        </div>

        <p>
          {{ 'client.fused.jobs.no_results.paragraph' | t }}
        </p>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import JobTeaser from '@/vue/components/fused/job_teaser'

export default {
  components: { JobTeaser },
  data: () => ({
    mediaQuery: null
  }),
  computed: {
    ...mapState([
      'results',
      'resultsPagination',
      'bookmarks',
      'resultsLoading',
      'resultsJobSelected',
      'resultsJobUserSelected',
      'resultsMatchingProfile',
      'isSearchSticky',
      'pageType',
      'jobPinned',
      'termSuggestions',
      'resultsCount'
    ]),
    ...mapGetters(['resultsUpToDate', 'bookmarksMap', 'isUserLoggedIn']),
    jobPinnedId() {
      return (this.jobPinned || {}).id
    },
    hasJobPinnedId() {
      return this.jobPinnedId !== null && this.jobPinnedId !== undefined
    },
    idxOffset() {
      return this.hasJobPinnedId ? 2 : 1
    },
    jobs() {
      return this.hasJobPinnedId
        ? this.results.filter(job => job.id !== this.jobPinnedId)
        : this.results
    }
  },
  watch: {
    resultsJobSelected(jobId, oldJobId) {
      this.scrollSelectedJobIntoView(jobId, oldJobId)
    },
    resultsJobUserSelected(isSelected) {
      // This is only relevant for mobile: When coming out of the detail view, we want to scroll the selected job into
      // focus.
      if (!isSelected) {
        this.scrollSelectedJobIntoView()
      }
    }
  },
  mounted() {
    // This is not called in SSR
    // The foundation sites js has client specific js that is always executed, so we need to make sure to only import it
    // when we're in the client.
    const {
      MediaQuery
    } = require('foundation-sites/js/foundation.util.mediaQuery')
    MediaQuery._init()
    this.mediaQuery = MediaQuery

    // Get user specfic bookmarks
    if (this.isUserLoggedIn) {
      this.bookmarksGet()
    }
    if (this.pageType === 'JOB') {
      // for single job pages, we do not select the first job, instead we "fake" the teaser for the requested job
      // onto the top of the list and hide the actual teaser
    } else {
      // Select first job in list on initial page load
      // All following selects will automatically be handled in the store action.
      this.resultsSelectFirst()
    }
  },
  methods: {
    ...mapActions([
      'resultsJobUserSelect',
      'bookmarksGet',
      'bookmarksToggle',
      'resultsPaginationNext',
      'resultsSelectFirst'
    ]),
    onSelect(job) {
      this.resultsJobUserSelect({
        jobId: job.id,
        fullPath: job.full_path,
        title: job.metaTitle
      })
    },
    scrollSelectedJobIntoView(jobId, oldJobId) {
      if (this.resultsJobSelected) {
        this.$nextTick(() => {
          // Unfortunately, we can not use the Vue $refs feature, as they are not dynamic and only updated sporadically.
          const [el] = this.$refs.jobList.getElementsByClassName(
            'fsd-list__item--selected'
          )
          if (!el) return

          const [anchorEl] = el.getElementsByClassName('fsd-list__item-anchor')
          if (!anchorEl) return

          setTimeout(() => {
            try {
              const block = this.resultsJobUserSelected ? 'center' : 'end'
              // do not animate scroll when on mobile our auto-selecting the first job (no prior job was set).
              const behavior =
                this.mediaQuery.current !== 'large' || !oldJobId
                  ? 'auto'
                  : 'smooth'
              anchorEl.scrollIntoView({ block, inline: 'nearest', behavior })
            } catch (e) {
              // fallback to end or start for older browsers
              anchorEl.scrollIntoView(this.resultsJobUserSelected)
            }
          })
        })
      }
    }
  }
}
</script>
